import { useRef, useEffect, useCallback } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { z } from 'zod';
import { characterFormMessages } from './characterForm.messages';
import type {
  StoryTemplate,
  CharacterWithUrls,
  CharacterSelection,
} from 'schemas';
import { CharacterSelectionArraySchema } from 'schemas';
import { sharedMessages } from '@/shared-messages/shared.messages';

import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { CharacterFormState } from 'schemas';
import { Heading3, Paragraph, Small } from '@/components/Typography';
import { ImageWithFallback } from '@/components/ImageWithFallback';

interface CharacterFormProps {
  storyRoles: StoryTemplate['story_template_roles'] | null;
  formData: CharacterFormState[];
  onFormDataUpdate: (formData: CharacterFormState[]) => void;
  onSubmit: (
    formData: CharacterFormState[],
    characterSelections: CharacterSelection[]
  ) => Promise<void>;
  isSubmitting?: boolean;
  existingCharacters?: CharacterWithUrls[];
}

type CharacterFormValues = z.infer<typeof CharacterSelectionArraySchema>;

export function CharacterForm({
  storyRoles,
  formData,
  onFormDataUpdate,
  onSubmit,
  isSubmitting = false,
  existingCharacters = [],
}: CharacterFormProps) {
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  // Initialize character selections based on form data
  const initialCharacterSelections = formData.map(
    (char: CharacterFormState) => ({
      type: 'new' as const,
      existingCharacterId: undefined,
      newCharacter: {
        name: char.name,
        age: char.age,
        gender: char.gender,
        reference_image: char.reference_image,
      },
    })
  );

  const form = useForm<CharacterFormValues>({
    resolver: zodResolver(CharacterSelectionArraySchema),
    defaultValues: {
      characterSelections: initialCharacterSelections,
    },
    mode: 'onChange',
  });

  const { fields } = useFieldArray({
    control: form.control,
    name: 'characterSelections',
  });

  // Update parent form data when form values change
  const handleFormChange = useCallback(
    (data: CharacterFormValues) => {
      const updatedFormData: CharacterFormState[] =
        data.characterSelections.map((selection, _index) => {
          if (selection.type === 'existing' && selection.existingCharacterId) {
            // Find the existing character
            const existingChar = existingCharacters.find(
              (char) => char.id === selection.existingCharacterId
            );
            if (existingChar) {
              return {
                name: existingChar.name,
                age: existingChar.age,
                gender: existingChar.gender,
                reference_image: null, // Existing characters don't need new images
              };
            }
          }

          // For new characters or fallback
          return {
            name: selection.newCharacter?.name || '',
            age: selection.newCharacter?.age,
            gender: selection.newCharacter?.gender,
            reference_image: selection.newCharacter?.reference_image || null,
          };
        });
      onFormDataUpdate(updatedFormData);
    },
    [onFormDataUpdate, existingCharacters]
  );

  // Watch form changes and update parent
  useEffect(() => {
    const subscription = form.watch((value) => {
      if (value.characterSelections) {
        handleFormChange(value as CharacterFormValues);
      }
    });
    return () => subscription.unsubscribe();
  }, [form, handleFormChange]);

  // Handle form submission
  const handleFormSubmit = async (data: CharacterFormValues) => {
    // Extract only new characters for submission
    const newCharacters: CharacterFormState[] = data.characterSelections
      .filter(
        (
          selection
        ): selection is Extract<
          typeof selection,
          { type: 'new'; newCharacter: CharacterFormState }
        > => selection.type === 'new' && Boolean(selection.newCharacter)
      )
      .map((selection) => selection.newCharacter);

    await onSubmit(newCharacters, data.characterSelections);
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleFormSubmit)}
        className="space-y-8"
      >
        <div className="space-y-8">
          {fields.map((field, index) => (
            <div key={field.id}>
              <>
                <Heading3 className="mb-4">
                  {storyRoles && storyRoles[index]
                    ? storyRoles[index]?.title
                    : characterFormMessages.heading}
                </Heading3>
                <Paragraph className="mb-6">
                  {storyRoles && storyRoles[index]
                    ? storyRoles[index]?.description
                    : characterFormMessages.subheading}
                </Paragraph>
              </>

              {/* Character Selection Type - Only show if there are existing characters */}
              {/* ONLY enabled in dev mode */}
              {import.meta.env.DEV && (
                <>
                  {existingCharacters.length > 0 && (
                    <FormField
                      control={form.control}
                      name={`characterSelections.${index}.type`}
                      render={({ field }) => (
                        <FormItem className="mb-6">
                          <FormLabel>
                            {characterFormMessages.characterSelectionLabel}

                            <Small className="text-destructive ml-1">
                              {sharedMessages.devOnlyIndicator}
                            </Small>
                          </FormLabel>
                          <Select
                            onValueChange={(value) => {
                              field.onChange(value);
                              // Clear the other field when switching types
                              if (value === 'existing') {
                                form.setValue(
                                  `characterSelections.${index}.newCharacter`,
                                  undefined
                                );
                              } else if (value === 'new') {
                                form.setValue(
                                  `characterSelections.${index}.existingCharacterId`,
                                  undefined
                                );
                              }
                            }}
                            value={field.value}
                          >
                            <FormControl>
                              <SelectTrigger className="w-full">
                                <SelectValue
                                  placeholder={
                                    characterFormMessages.characterSelectionPlaceholder
                                  }
                                />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="existing">
                                {characterFormMessages.useExistingCharacter}
                              </SelectItem>
                              <SelectItem value="new">
                                {characterFormMessages.createNewCharacter}
                              </SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}

                  {/* Existing Character Selection */}
                  {existingCharacters.length > 0 &&
                    form.watch(`characterSelections.${index}.type`) ===
                      'existing' && (
                      <FormField
                        control={form.control}
                        name={`characterSelections.${index}.existingCharacterId`}
                        render={({ field }) => (
                          <FormItem className="mb-6">
                            <FormLabel>
                              {characterFormMessages.selectExistingCharacter}
                              {import.meta.env.DEV && (
                                <Small className="text-destructive ml-1">
                                  {sharedMessages.devOnlyIndicator}
                                </Small>
                              )}
                            </FormLabel>
                            <Select
                              onValueChange={field.onChange}
                              value={field.value}
                            >
                              <FormControl>
                                <SelectTrigger className="w-full">
                                  <SelectValue
                                    placeholder={
                                      characterFormMessages.selectExistingCharacterPlaceholder
                                    }
                                  />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {existingCharacters.map((character) => (
                                  <SelectItem
                                    key={character.id}
                                    value={character.id}
                                  >
                                    {character.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    )}
                </>
              )}

              {/* New Character Form - Show when no existing characters OR when "new" is selected */}
              {(existingCharacters.length === 0 ||
                form.watch(`characterSelections.${index}.type`) === 'new') && (
                <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
                  <div className="space-y-6">
                    <FormField
                      control={form.control}
                      name={`characterSelections.${index}.newCharacter.name`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            {characterFormMessages.characterNameLabel}
                          </FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              placeholder={
                                characterFormMessages.characterNamePlaceholder
                              }
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name={`characterSelections.${index}.newCharacter.age`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            {characterFormMessages.ageLabel}
                          </FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              type="number"
                              min={1}
                              max={120}
                              placeholder={characterFormMessages.agePlaceholder}
                              value={field.value ?? ''} // Use nullish coalescing to handle undefined and avoid coercing schema between string and number
                              onChange={(e) => {
                                // This allows to avoid coercing between string and number in the schema and api request
                                const value = e.target.value;
                                field.onChange(
                                  value === ''
                                    ? undefined
                                    : parseInt(value) || undefined
                                );
                              }}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name={`characterSelections.${index}.newCharacter.gender`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            {characterFormMessages.genderLabel}
                          </FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            value={field.value}
                          >
                            <FormControl>
                              <SelectTrigger className="w-full">
                                <SelectValue
                                  placeholder={
                                    characterFormMessages.genderPlaceholder
                                  }
                                />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="male">
                                {sharedMessages.genderLabels.male}
                              </SelectItem>
                              <SelectItem value="female">
                                {sharedMessages.genderLabels.female}
                              </SelectItem>
                              <SelectItem value="gender_neutral">
                                {sharedMessages.genderLabels.gender_neutral}
                              </SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div>
                    <FormField
                      control={form.control}
                      name={`characterSelections.${index}.newCharacter.reference_image`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            {characterFormMessages.photoLabel}
                          </FormLabel>
                          <FormControl>
                            <div
                              className="cursor-pointer rounded-lg border-2 border-dashed p-8 text-center transition-colors hover:border-stone-400"
                              onClick={() =>
                                fileInputRefs.current[index]?.click()
                              }
                            >
                              <input
                                ref={(el) => {
                                  fileInputRefs.current[index] = el;
                                }}
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  field.onChange(file);
                                }}
                              />

                              {field.value ? (
                                <div className="space-y-4">
                                  <div className="mx-auto h-32 w-32 overflow-hidden rounded-lg bg-stone-100">
                                    <ImageWithFallback
                                      src={URL.createObjectURL(
                                        field.value as File
                                      )}
                                      alt={
                                        characterFormMessages.characterPreviewAlt
                                      }
                                      className="h-full w-full object-cover"
                                    />
                                  </div>
                                  <div className="max-w-full">
                                    <Small>
                                      {(
                                        (field.value as File).size /
                                        1024 /
                                        1024
                                      ).toFixed(2)}{' '}
                                      {characterFormMessages.fileSizeMB}
                                    </Small>
                                  </div>
                                  <Small>
                                    {characterFormMessages.clickToChangeImage}
                                  </Small>
                                </div>
                              ) : (
                                <div className="space-y-4">
                                  <div className="mx-auto h-12 w-12 text-stone-400">
                                    <svg
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                                      />
                                    </svg>
                                  </div>
                                  <div className="flex flex-col gap-1">
                                    <Small>
                                      {characterFormMessages.photoPlaceholder}
                                    </Small>
                                    <Small className="mt-1">
                                      {
                                        characterFormMessages.photoSupportedFormats
                                      }
                                    </Small>
                                  </div>
                                </div>
                              )}
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-8 flex justify-end">
          <Button
            type="submit"
            variant="default"
            disabled={!form.formState.isValid || isSubmitting}
          >
            {isSubmitting
              ? sharedMessages.loadingStates.makingMagic
              : storyRoles && storyRoles?.length > 1
                ? characterFormMessages.createCharacters
                : characterFormMessages.createCharacter}
          </Button>
        </div>
      </form>
    </Form>
  );
}
